'use client'

import * as React from 'react'

import {
  isEditableTarget,
  matchesShortcut,
  type ShortcutKey,
} from '../../../lib/keyboard-shortcuts'

type ShortcutRegistration = {
  keys: ShortcutKey[]
  handler: () => void
  enabled?: () => boolean
}

type KeyboardShortcutsContextValue = {
  registerShortcut: (id: string, registration: ShortcutRegistration) => void
  unregisterShortcut: (id: string) => void
}

const KeyboardShortcutsContext =
  React.createContext<KeyboardShortcutsContextValue | null>(null)

export function KeyboardShortcutsProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const shortcutsRef = React.useRef(new Map<string, ShortcutRegistration>())

  const registerShortcut = React.useCallback(
    (id: string, registration: ShortcutRegistration) => {
      shortcutsRef.current.set(id, registration)
    },
    [],
  )

  const unregisterShortcut = React.useCallback((id: string) => {
    shortcutsRef.current.delete(id)
  }, [])

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isEditableTarget(event.target)) return

      for (const registration of Array.from(shortcutsRef.current.values())) {
        if (!matchesShortcut(event, registration.keys)) continue
        if (registration.enabled && !registration.enabled()) continue

        event.preventDefault()
        event.stopPropagation()
        event.stopImmediatePropagation()
        registration.handler()
        return
      }
    }

    const listenerOptions: AddEventListenerOptions = {
      capture: true,
      passive: false,
    }

    window.addEventListener('keydown', handleKeyDown, listenerOptions)
    return () => {
      window.removeEventListener('keydown', handleKeyDown, listenerOptions)
    }
  }, [])

  const value = React.useMemo(
    () => ({
      registerShortcut,
      unregisterShortcut,
    }),
    [registerShortcut, unregisterShortcut],
  )

  return (
    <KeyboardShortcutsContext.Provider value={value}>
      {children}
    </KeyboardShortcutsContext.Provider>
  )
}

export function useKeyboardShortcut({
  id,
  keys,
  handler,
  enabled,
}: {
  id: string
  keys: ShortcutKey[]
  handler: () => void
  enabled?: () => boolean
}) {
  const context = React.useContext(KeyboardShortcutsContext)

  if (!context) {
    throw new Error(
      'useKeyboardShortcut must be used within KeyboardShortcutsProvider',
    )
  }

  const handlerRef = React.useRef(handler)
  const enabledRef = React.useRef(enabled)

  React.useEffect(() => {
    handlerRef.current = handler
  }, [handler])

  React.useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  const keysKey = keys.join('+')

  React.useEffect(() => {
    context.registerShortcut(id, {
      keys,
      handler: () => handlerRef.current(),
      enabled: enabledRef.current
        ? () => enabledRef.current?.() ?? true
        : undefined,
    })

    return () => {
      context.unregisterShortcut(id)
    }
  }, [context, id, keysKey, keys])
}
